import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navItems = [
  { label: "首页", to: "/" },
  { label: "视频", to: "/videos" },
  { label: "专题", to: "/special/s1" },
  { label: "搜索", to: "/search" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setNewsOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to.split("/")[1] ? `/${to.split("/")[1]}` : to);

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50/80 backdrop-blur"
        }`}
      >
        <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[68px] md:px-6">
          <div className="flex items-center gap-6 lg:gap-8">
            <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500">
                <i className="ri-fire-fill text-lg text-background-50"></i>
              </span>
              <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">
                91<span className="text-primary-500">.con</span>
              </span>
            </Link>

            <nav className="hidden items-center gap-1 md:flex">
              <Link
                to="/"
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname === "/"
                    ? "text-primary-400"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                首页
              </Link>

              <div
                className="relative"
                onMouseEnter={() => setNewsOpen(true)}
                onMouseLeave={() => setNewsOpen(false)}
              >
                <button
                  type="button"
                  className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    location.pathname.startsWith("/category")
                      ? "text-primary-400"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  资讯
                  <i className="ri-arrow-down-s-line text-base"></i>
                </button>
                {newsOpen && (
                  <div className="absolute left-0 top-full w-52 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-1.5 shadow-lg">
                    {categories.map((c) => (
                      <Link
                        key={c.key}
                        to={`/category/${c.key}`}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200 hover:text-foreground-950"
                      >
                        <i className="ri-article-line text-primary-400"></i>
                        {c.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {navItems.slice(1).map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.to)
                      ? "text-primary-400"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/search"
              className="hidden h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-400 sm:flex"
              aria-label="搜索"
            >
              <i className="ri-search-line"></i>
            </Link>
            <Link
              to="/special/s1"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:block"
            >
              热点专题
            </Link>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-800 md:hidden"
              aria-label="打开菜单"
            >
              <i className="ri-menu-line text-lg"></i>
            </button>
          </div>
        </div>
      </header>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-background-50/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-72 border-l border-background-200 bg-background-100 p-5">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-heading text-lg font-bold text-foreground-950">导航</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700"
                aria-label="关闭菜单"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              <Link
                to="/"
                className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-200"
              >
                <i className="ri-home-5-line text-primary-400"></i>首页
              </Link>
              <div className="mt-1 px-3 text-xs font-semibold uppercase tracking-wider text-foreground-500">
                资讯分类
              </div>
              {categories.map((c) => (
                <Link
                  key={c.key}
                  to={`/category/${c.key}`}
                  className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-200"
                >
                  <i className="ri-article-line text-primary-400"></i>
                  {c.name}
                </Link>
              ))}
              <div className="my-2 h-px bg-background-200"></div>
              {[
                { label: "视频资源", to: "/videos", icon: "ri-play-circle-line" },
                { label: "热点专题", to: "/special/s1", icon: "ri-fire-line" },
                { label: "搜索", to: "/search", icon: "ri-search-line" },
                { label: "关于我们", to: "/about", icon: "ri-information-line" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-200"
                >
                  <i className={`${item.icon} text-primary-400`}></i>
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      <MobileTabBar />
    </>
  );
}

function MobileTabBar() {
  const location = useLocation();
  const tabs = [
    { label: "首页", to: "/", icon: "ri-home-5-line", activeIcon: "ri-home-5-fill" },
    { label: "视频", to: "/videos", icon: "ri-play-circle-line", activeIcon: "ri-play-circle-fill" },
    { label: "专题", to: "/special/s1", icon: "ri-fire-line", activeIcon: "ri-fire-fill" },
    { label: "搜索", to: "/search", icon: "ri-search-line", activeIcon: "ri-search-line" },
    { label: "关于", to: "/about", icon: "ri-information-line", activeIcon: "ri-information-fill" },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-background-200 bg-background-100/95 backdrop-blur md:hidden">
      <div className="flex items-stretch justify-around">
        {tabs.map((t) => {
          const active =
            t.to === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(`/${t.to.split("/")[1]}`);
          return (
            <Link
              key={t.to}
              to={t.to}
              className={`flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] ${
                active ? "text-primary-400" : "text-foreground-600"
              }`}
            >
              <i className={`${active ? t.activeIcon : t.icon} text-lg`}></i>
              {t.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}